import React, { useEffect, useMemo, useState } from "react";
import styles from "./Style.module.css";
import { Pencil } from "lucide-react";
import { Counter } from "../Counter/Counter";
import { Snakbar } from "../Snakbar/Snakbar";
import { getVoucherUnitPrice } from "../../Context/Voucher/VoucherContext";
import { maxVoucherCount } from "../../Utils/voucherConfig";
import VoucherSubtotal from "../VoucherSubtotal/VoucherSubtotal";
import { calculateGST } from "../../Utils/Helper";
import { RemoveVoucherPopup } from "../Popup/RemoveVoucherPopup/RemoveVoucherPopup";

const groupVouchersByCategory = (selectedVouchers = []) => {
  const categories = selectedVouchers.reduce((acc, voucher) => {
    const categoryKey = voucher.category_id ?? "uncategorized";

    if (!acc[categoryKey]) {
      acc[categoryKey] = {
        category_id: voucher.category_id ?? null,
        category_name: voucher.category_name || "Vouchers",
        vouchersById: {},
      };
    }

    const existing = acc[categoryKey].vouchersById[voucher.id];
    if (!existing) {
      // Slot-based cart: each selectedVouchers entry is one unit.
      acc[categoryKey].vouchersById[voucher.id] = { ...voucher, count: 1 };
    } else {
      acc[categoryKey].vouchersById[voucher.id] = {
        ...existing,
        ...voucher,
        count: (Number(existing.count) || 0) + 1,
      };
    }

    return acc;
  }, {});

  return Object.values(categories).map((category) => ({
    category_id: category.category_id,
    category_name: category.category_name,
    vouchers: Object.values(category.vouchersById),
  }));
};

const formatVoucherName = (voucher) => {
  if (voucher?.duration) {
    return `${voucher.duration} Mins ${voucher.name}`;
  }
  return voucher?.name || "";
};

const formatPrice = (price, forceCents = false) => {
  const value = Number(price) || 0;
  if (forceCents) return value.toFixed(2);
  return Number.isInteger(value) ? value.toString() : value.toFixed(2);
};

/** Service vouchers (with duration) attract GST; cash-style vouchers show GST $0. */
const categoryAppliesGst = (vouchers = []) =>
  vouchers.some((voucher) => Number(voucher?.duration) > 0);

const VoucherDetails = ({
  selectedVouchers = [],
  editMode = true,
  onIncrementVoucher,
  onDecrementVoucher,
  onEditCategory,
  totalSelectedCount = 0,
  onTotalsChange,
}) => {
  const [getMaxVouchers, setGetMaxVouchers] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [voucherToRemove, setVoucherToRemove] = useState(null);

  const groupedVouchers = useMemo(
    () => groupVouchersByCategory(selectedVouchers),
    [selectedVouchers]
  );

  const categoryTotals = useMemo(() => {
    return groupedVouchers.map((category) => {
      const itemsTotal = category.vouchers.reduce(
        (sum, voucher) =>
          sum + getVoucherUnitPrice(voucher) * (Number(voucher.count) || 0),
        0
      );
      const appliesGst = categoryAppliesGst(category.vouchers);
      const gstAmount = appliesGst ? Number(calculateGST(itemsTotal)) || 0 : 0;
      const subtotal = itemsTotal + gstAmount;
      return {
        category_id: category.category_id,
        itemsTotal,
        gstAmount,
        subtotal,
      };
    });
  }, [groupedVouchers]);

  const grandTotal = useMemo(
    () => categoryTotals.reduce((sum, row) => sum + row.subtotal, 0),
    [categoryTotals]
  );

  const isMaxReached = Number(totalSelectedCount) >= maxVoucherCount;

  useEffect(() => {
    onTotalsChange?.({
      grandTotal,
      categoryTotals,
    });
  }, [grandTotal, categoryTotals, onTotalsChange]);

  useEffect(() => {
    if (getMaxVouchers) {
      setShowSnackbar(true);
      const hideTimeout = setTimeout(() => {
        setShowSnackbar(false);
        const cleanupTimeout = setTimeout(() => setGetMaxVouchers(false), 300);
        return () => clearTimeout(cleanupTimeout);
      }, 3000);

      return () => clearTimeout(hideTimeout);
    }
  }, [getMaxVouchers]);

  const handleIncrementClick = (voucher) => {
    if (isMaxReached) {
      setGetMaxVouchers(true);
      return;
    }
    onIncrementVoucher?.(voucher);
  };

  const handleDecrementClick = (voucher) => {
    const displayCount = Number(voucher?.count) || 0;
    if (editMode && displayCount === 1) {
      setVoucherToRemove(voucher);
      return;
    }
    onDecrementVoucher?.(voucher);
  };

  const handleCloseRemovePopup = () => {
    setVoucherToRemove(null);
  };

  const handleConfirmRemoveVoucher = () => {
    if (voucherToRemove) {
      onDecrementVoucher?.(voucherToRemove);
    }
    setVoucherToRemove(null);
  };

  return (
    <div className={styles.voucherList}>
      {getMaxVouchers && (
        <Snakbar
          text={`You can add up to ${maxVoucherCount} vouchers.`}
          active={showSnackbar}
          varient="danger"
        />
      )}

      <RemoveVoucherPopup
        active={Boolean(voucherToRemove)}
        onClose={handleCloseRemovePopup}
        onConfirmRemove={handleConfirmRemoveVoucher}
      />

      {groupedVouchers.map((category, categoryIndex) => {
        const totals = categoryTotals[categoryIndex] || {
          itemsTotal: 0,
          gstAmount: 0,
          subtotal: 0,
        };

        return (
          <div
            key={category.category_id ?? category.category_name}
            className={`${styles.categoryBlock}${
              !editMode ? ` ${styles.categoryBlockReview}` : ""
            }${
              !editMode && categoryIndex === groupedVouchers.length - 1
                ? ` ${styles.lastCategoryBlockReview}`
                : ""
            }`}
          >
            <div className={styles.categoryHeader}>
              <h3 className={styles.categoryName}>{category.category_name}</h3>
              {editMode && (
                <button
                  type="button"
                  className={`${styles.categoryEditBtn} btn`}
                  onClick={() => onEditCategory?.(category)}
                  title={`Edit ${category.category_name}`}
                  aria-label={`Edit ${category.category_name}`}
                >
                  <Pencil size={16} color="var(--PrimaryColor900)" />
                </button>
              )}
            </div>

            <div className={styles.categoryVouchers}>
              {category.vouchers.map((voucher) => {
                const unitPrice = getVoucherUnitPrice(voucher);
                const displayCount = voucher.count || 0;
                const lineTotal = unitPrice * displayCount;

                return (
                  <div key={voucher.id} className={styles.voucherItem}>
                    {editMode ? (
                      <div className={styles.voucherItemLeft}>
                        <div className={styles.voucherName}>
                          {formatVoucherName(voucher)}
                        </div>
                        <div className={styles.voucherPrice}>
                          S${formatPrice(lineTotal)}
                        </div>
                      </div>
                    ) : (
                      <div className={styles.voucherItemLeft}>
                        <div className={styles.voucherNameReview}>
                          {displayCount}x {formatVoucherName(voucher)}
                        </div>
                      </div>
                    )}

                    <div className={styles.voucherItemRight}>
                      {editMode ? (
                        <Counter
                          id={voucher.id}
                          isCounterVisible={displayCount > 0}
                          count={displayCount}
                          disabled={isMaxReached}
                          handleShowCounter={() =>
                            handleIncrementClick({
                              ...voucher,
                              count: 0,
                            })
                          }
                          handleIncrement={() => handleIncrementClick(voucher)}
                          handleDecrement={() => handleDecrementClick(voucher)}
                        />
                      ) : (
                        <div className={styles.voucherLineTotal}>
                          S${formatPrice(lineTotal, true)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {!editMode && (
              <VoucherSubtotal
                itemsTotal={totals.itemsTotal}
                gstAmount={totals.gstAmount}
              />
            )}
          </div>
        );
      })}

      {!editMode && groupedVouchers.length > 0 && (
        <div className={styles.grandTotalRow}>
          <div className={styles.grandTotalLabel}>Total</div>
          <div className={styles.grandTotalValue}>
            S${formatPrice(grandTotal, true)}
          </div>
        </div>
      )}
    </div>
  );
};

export default VoucherDetails;
